import { Calendar, ArrowRight, BookOpen, Loader2, Eye, ThumbsUp } from 'lucide-react'; 
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategories } from '../../hooks/useCategories';
import { useAllBlogs } from '../../hooks/expertBlogHooks/useViewBlogs'; 
import { optimizeCloudinaryImage } from '../experts/imageHelper/ImageCompressorHelper';

const SKELETON_CATEGORIES = Array.from({ length: 6 }, (_, i) => ({
  id: `skeleton-${i}`,
  name: '',
  bg: 'from-gray-300 to-gray-200',
}));

export function BlogHomepage() {
  const navigate = useNavigate();
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories();
  
  const { allBlogs, isLoadingBlogs } = useAllBlogs();
  
  const displayCategories = categoriesLoading || categoriesError || categories.length === 0 
    ? SKELETON_CATEGORIES 
    : categories;
  
  const [activeCategoryId, setActiveCategoryId] = useState<string | number | null>(null);
  const currentCategoryId = activeCategoryId || (categories.length > 0 ? categories[0]?.id : null);

  const filteredBlogs = allBlogs.filter((blog) => {
    const blogCatId = blog.categoryId || blog.category_id;
    return String(blogCatId) === String(currentCategoryId);
  });

  // 🟢 Extract only the first 3 blogs for the homepage display
  const displayedBlogs = filteredBlogs.slice(0, 3);
  const hasMoreBlogs = filteredBlogs.length > 3;

  return (
    <section className="py-12 sm:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-3xl sm:text-4xl text-gray-900 mb-3">
            Latest Insights
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            Expert advice and tips to help you succeed
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8">
          {displayCategories.map((category) => {
            const isCategoriesLoading = !category.name;
            
            return (
              <button
                key={category.id}
                onClick={() => !isCategoriesLoading && setActiveCategoryId(category.id)}
                disabled={isCategoriesLoading}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl transition-all text-sm sm:text-base ${
                  isCategoriesLoading
                    ? 'bg-gray-200 animate-pulse cursor-not-allowed'
                    : String(currentCategoryId) === String(category.id) 
                    ? 'bg-brand-blue-900 text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-brand-blue-800 hover:text-brand-blue-800'
                }`}
              >
                {isCategoriesLoading ? (
                  <span className="invisible">Loading</span>
                ) : (
                  category.name
                )}
              </button>
            );
          })}
        </div>

        {categoriesError && (
          <div className="mb-6 text-center">
            <p className="text-sm text-red-600">
              Unable to load categories. Mr. Developer working on it.
            </p>
          </div>
        )}

        {isLoadingBlogs ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-brand-blue-900" />
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No blogs found</h3>
            <p className="text-gray-500 text-center max-w-sm">
              Our experts haven't published any articles in this category yet. Check back soon!
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {/* 🟢 Map through displayedBlogs (max 3) instead of filteredBlogs */}
              {displayedBlogs.map((blog) => {
                const categoryConfig = categories.find(
                  (cat) => String(cat.id) === String(currentCategoryId)
                );
                const categoryBg = categoryConfig?.bg || 'from-gray-400 to-gray-300';
                const categoryName = categoryConfig?.name || 'General';

              const originalImage =
  blog.cover_image ||
  blog.coverImage ||
  'https://images.unsplash.com/photo-1565489030990-e211075fe11c';

const displayImage = optimizeCloudinaryImage(originalImage, 800, 450);

                const plainTextContent = blog.content?.replace(/[#*`_]/g, '') || '';
                const excerpt = plainTextContent.length > 200 ? plainTextContent.substring(0, 200) : plainTextContent;

                return (
                  <article
                    key={blog.id}
                    className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col"
                  >
                    <div className="relative overflow-hidden shrink-0">
                     <img
  src={displayImage}
  alt={blog.title}
  loading="lazy"
  decoding="async"
  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
/>

                      <div className="absolute top-4 left-4">
                        <span
                          className={`
                            px-3 py-1 rounded-full text-xs font-medium text-white shadow-sm
                            bg-gradient-to-br ${categoryBg}
                          `}
                        >
                          {categoryName}
                        </span>
                      </div>
                    </div>

                    <div className="p-6 space-y-4 flex flex-col flex-1">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {blog.title}
                      </h3>

                      <div className="flex justify-start items-center gap-4 text-xs font-semibold text-gray-500">
                          <span className="flex items-center gap-1.5">
                            <Eye className="w-4 h-4 text-gray-400" /> {Number(blog.views || 0).toLocaleString()} Views
                          </span>
                          <span className="flex items-center gap-1.5">
                            <ThumbsUp className="w-4 h-4 text-gray-400" /> {Number(blog.likes || 0).toLocaleString()} Likes
                          </span>
                      </div>

                      <p className="text-gray-600 text-sm line-clamp-2 flex-1">
                        {excerpt}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                        
                        <div className="text-sm font-semibold text-gray-800">
                          by {blog.expert_name || blog.expertName || "Expert"}
                        </div>
                        
                        <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {new Date(blog.created_at || blog.createdAt || new Date()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>

                      </div>

                      {/* 🟢 DIRECT NAVIGATION: Let everyone read the blog! */}
                      <button 
                        onClick={() => navigate(`/blog/${blog.id}`)}
                        className="w-full py-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors flex items-center justify-center gap-2 mt-2 bg-blue-50 hover:bg-blue-100 rounded-lg"
                      >
                        Read More
                        <ArrowRight className="w-4 h-4 transition-all" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* 🟢 View More Button */}
            {hasMoreBlogs && (
              <div className="mt-10 flex justify-center">
                <button
                  onClick={() => navigate('/blogs')}
                  className="flex items-center gap-2 px-8 py-3 bg-white text-brand-blue-900 font-semibold rounded-xl border-2 border-brand-blue-900 hover:bg-brand-blue-900 hover:text-white transition-all duration-300 shadow-sm"
                >
                  View More Articles <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}